import React, { useEffect, useState } from 'react';
import { FaPlus, FaTrash, FaEdit } from 'react-icons/fa';
import * as staffApi from '../services/staffApi';
import { useMerchantAuth } from '../../../contexts/AuthContext';

export default function RolesTab() {
    const { hasPermission } = useMerchantAuth();
    const [roles, setRoles] = useState([]);
    const [, setPermissionKeys] = useState([]);
    const [permissionDomains, setPermissionDomains] = useState([]);
    const [permissionLabels, setPermissionLabels] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [formModal, setFormModal] = useState({ show: false, role: null });
    const [formName, setFormName] = useState('');
    const [formPermissions, setFormPermissions] = useState(new Set());
    const [saving, setSaving] = useState(false);
    const [deleteModal, setDeleteModal] = useState({ show: false, role: null });

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const [rolesRes, keysRes] = await Promise.all([
                staffApi.listRoles(),
                staffApi.getPermissionKeys()
            ]);
            setRoles(rolesRes.data || []);
            const keys = keysRes.data?.permissionKeys || [];
            const domains = keysRes.data?.permissionDomains || [];
            setPermissionKeys(keys);
            setPermissionDomains(domains);
            const labels = {};
            domains.forEach((d) => {
                (d.permissions || []).forEach((p) => {
                    labels[p.key] = p.label;
                });
            });
            setPermissionLabels(labels);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to load roles');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const openCreate = () => {
        setFormName('');
        setFormPermissions(new Set());
        setFormModal({ show: true, role: null });
    };

    const openEdit = (role) => {
        setFormName(role.name);
        setFormPermissions(new Set((role.permissions || []).map((p) => p.permission_key)));
        setFormModal({ show: true, role });
    };

    const togglePermission = (key) => {
        setFormPermissions((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const handleSave = async () => {
        if (!formName.trim()) {
            return;
        }
        setSaving(true);
        setError(null);
        try {
            if (formModal.role) {
                await staffApi.updateRole(formModal.role.id, {
                    name: formName.trim(),
                    permissionKeys: Array.from(formPermissions)
                });
            } else {
                await staffApi.createRole(formName.trim(), Array.from(formPermissions));
            }
            setFormModal({ show: false, role: null });
            fetchData();
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to save role');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteModal.role) {
            return;
        }
        try {
            await staffApi.deleteRole(deleteModal.role.id);
            setDeleteModal({ show: false, role: null });
            fetchData();
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to delete role');
        }
    };

    const canDelete = (role) => role.name?.toLowerCase() !== 'owner';

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-tutorial="roles-page">
            <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-800">Roles & Permissions</h2>
                {hasPermission('add_role') && (
                <button
                    onClick={openCreate}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium"
                >
                    <FaPlus /> Add Role
                </button>
                )}
            </div>

            {error && (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
            )}

            <div className="bg-white rounded-lg shadow overflow-hidden">
                {roles.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">No roles yet. Create one above.</div>
                ) : (
                    <div className="divide-y">
                        {roles.map((role) => (
                            <div key={role.id} className="p-4 flex flex-wrap items-start justify-between gap-2">
                                <div>
                                    <div className="font-medium text-gray-800 flex items-center gap-2">
                                        {role.name}
                                        {role.is_default && (
                                            <span className="text-xs px-2 py-0.5 rounded bg-gray-200">default</span>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap gap-1 mt-2">
                                        {(role.permissions || []).map((p) => (
                                            <span
                                                key={p.id}
                                                className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary"
                                            >
                                                {permissionLabels[p.permission_key] || p.permission_key}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    {hasPermission('edit_role') && (
                                    <button
                                        onClick={() => openEdit(role)}
                                        className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                                        title="Edit role"
                                    >
                                        <FaEdit />
                                    </button>
                                    )}
                                    {canDelete(role) && hasPermission('delete_role') && (
                                        <button
                                            onClick={() => setDeleteModal({ show: true, role })}
                                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                                            title="Delete role"
                                        >
                                            <FaTrash />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Create/Edit modal */}
            {formModal.show && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
                        <h3 className="text-lg font-semibold mb-4">
                            {formModal.role ? 'Edit Role' : 'Add Role'}
                        </h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                <input
                                    type="text"
                                    value={formName}
                                    onChange={(e) => setFormName(e.target.value)}
                                    placeholder="e.g. Manager"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    disabled={formModal.role?.name === 'owner' && formModal.role?.is_default}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
                                <div className="space-y-4 max-h-64 overflow-y-auto">
                                    {permissionDomains.map((domain) => (
                                        <div key={domain.domain}>
                                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                                {domain.label}
                                            </div>
                                            <div className="space-y-2 pl-1">
                                                {(domain.permissions || []).map((p) => (
                                                    <label
                                                        key={p.key}
                                                        className="flex items-center gap-2 cursor-pointer"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={formPermissions.has(p.key)}
                                                            onChange={() => togglePermission(p.key)}
                                                            disabled={
                                                                formModal.role?.name === 'owner' &&
                                                                formModal.role?.is_default
                                                            }
                                                        />
                                                        <span>{p.label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2 mt-6">
                            <button
                                onClick={() => setFormModal({ show: false, role: null })}
                                className="flex-1 py-2 border rounded"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving || !formName.trim()}
                                className="flex-1 py-2 bg-primary text-white rounded disabled:opacity-50"
                            >
                                {saving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete confirmation */}
            {deleteModal.show && deleteModal.role && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <h3 className="text-lg font-semibold mb-2">Delete Role</h3>
                        <p className="text-gray-600 mb-4">
                            Delete role &quot;{deleteModal.role.name}&quot;? Staff with this role will lose these permissions.
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setDeleteModal({ show: false, role: null })}
                                className="flex-1 py-2 border rounded"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                className="flex-1 py-2 bg-red-600 text-white rounded"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
